import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CustomerSearchSelect from "@/components/CustomerSearchSelect";
import ApprovalDialog from "@/components/ApprovalDialog";
import ProductPickerModal from "@/components/ProductPickerModal";
import { fmtTL } from "@/components/StatusBadge";
import { curSym, activeCurrency } from "@/lib/company";
import {
  Plus, Trash2, Loader2, Package, FilePlus2, ShieldCheck, CheckCircle2, X,
  Users, FileSpreadsheet, Save, Search, Settings2, Download, Binoculars, RotateCcw,
  Maximize2, Minimize2,
} from "lucide-react";

const KINDS = [
  { k: "earchive", l: "e-Arşiv", d: "Bireysel / VKN'siz" },
  { k: "efatura", l: "e-Fatura", d: "Kurumsal / VKN'li" },
  { k: "despatch", l: "e-İrsaliye", d: "Sevk (tutarsız)" },
];
const VATS = [0, 1, 10, 20];
const emptyLine = () => ({ sku: "", name: "", unit: "Adet", quantity: 1, unit_price: 0, vat_rate: 20, discount: 0, description: "", extras: {}, product_id: null });

function productToLine(p) {
  const vat = Number(p?.vat_rate ?? 20);
  const gross = Number(p?.sales_prices?.market_retail) || Number((p?.channel_prices || []).find((c) => c.active && c.price > 0)?.price) || 0;
  const net = gross > 0 && vat > 0 ? Math.round((gross / (1 + vat / 100)) * 100) / 100 : gross;
  return { sku: p.sku || "", name: p.name || "", unit: p.base_unit || "Adet", quantity: 1, unit_price: net, vat_rate: vat, discount: 0, description: "", extras: {}, product_id: p.id };
}

// Satır içi ürün adı otomatik tamamlama — yazdıkça öneri çıkar, seçince satır dolar.
// Öneri menüsü portal + fixed konumla render edilir; üst kapsayıcıların overflow'u kırpmaz (3-5 sonuç kaydırmalı görünür).
function NameAutocomplete({ value, onChange, onPick, onFocus, onBinoculars, testid, containerRef }) {
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rect, setRect] = useState(null);
  const boxRef = useRef(null);
  const menuRef = useRef(null);
  // Menü DOM'a girer girmez native (passive:false) wheel bağlanır → fare menü üzerindeyken SAYFA değil MENÜ kayar.
  const bindMenu = useCallback((node) => {
    if (menuRef.current && menuRef.current._onWheel) {
      menuRef.current.removeEventListener("wheel", menuRef.current._onWheel);
    }
    menuRef.current = node;
    if (node) {
      const onWheel = (e) => { e.preventDefault(); e.stopPropagation(); node.scrollTop += e.deltaY; };
      node._onWheel = onWheel;
      node.addEventListener("wheel", onWheel, { passive: false });
    }
  }, []);
  const term = (value || "").trim();

  const place = useCallback(() => {
    const el = boxRef.current;
    const cont = containerRef?.current;
    if (!el || !cont) return;
    const r = el.getBoundingClientRect();
    const cr = cont.getBoundingClientRect();
    const menuH = 220;
    const below = window.innerHeight - r.bottom;
    const up = below < menuH + 16 && r.top > below;
    // Konum kaydırma kabının İÇERİK koordinatına göre (absolute) → panel kapla birlikte doğal kayar, dialog scroll kilidini aşmaz.
    const top = up ? (r.top - cr.top + cont.scrollTop - menuH - 4) : (r.bottom - cr.top + cont.scrollTop + 4);
    const left = r.left - cr.left + cont.scrollLeft;
    setRect({ top: Math.max(4, top), left, width: Math.max(r.width, 288) });
  }, [containerRef]);

  useEffect(() => {
    if (!open || term.length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const { data } = await api.get(`/products?q=${encodeURIComponent(term)}&page_size=8&channel=retail&match=contains`); setRes((data.items || []).slice(0, 8)); }
      catch { setRes([]); } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [term, open]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && boxRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const on = () => place();
    window.addEventListener("scroll", on, true);
    window.addEventListener("resize", on);
    return () => { window.removeEventListener("scroll", on, true); window.removeEventListener("resize", on); };
  }, [open, res.length, loading, place]);

  const showMenu = open && term.length >= 2 && (loading || res.length > 0);

  return (
    <div ref={boxRef} className="relative">
      <Input data-testid={testid} value={value} autoComplete="off" placeholder="Ürün adı…"
        onFocus={(e) => { onFocus?.(e); setOpen(true); place(); }}
        onChange={(e) => { onChange(e.target.value); setOpen(true); place(); }} className="h-8 w-full min-w-[150px] pr-8" />
      <button type="button" data-testid={`${testid}-binoculars`} onClick={onBinoculars} title="Ürün ara (dürbün · F12)"
        className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-slate-400 hover:bg-emerald-50 hover:text-emerald-700">
        <Binoculars className="h-3.5 w-3.5" />
      </button>
      {showMenu && rect && containerRef?.current && createPortal(
        <div ref={bindMenu} data-testid={`${testid}-suggest`}
          style={{ position: "absolute", top: rect.top, left: rect.left, width: rect.width, zIndex: 50 }}
          className="max-h-[220px] overflow-y-auto overscroll-contain rounded-md border border-slate-200 bg-white shadow-2xl">
          {loading && <div className="flex items-center gap-2 p-2 text-[11px] text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> Aranıyor…</div>}
          {!loading && res.map((p) => (
            <button key={p.id} type="button" data-testid={`${testid}-opt-${p.id}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(p); setOpen(false); }}
              className="flex w-full items-center gap-2 border-b border-slate-50 px-2.5 py-2 text-left hover:bg-emerald-50">
              <Package className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1"><p className="truncate text-xs font-medium text-slate-800">{p.name}</p><p className="truncate font-mono text-[10px] text-slate-400">{p.sku || "—"} · %{p.vat_rate ?? 20}</p></div>
            </button>
          ))}
        </div>, containerRef.current)}
    </div>
  );
}

function MoneyInput({ value, onChange, testid }) {
  return (
    <div className="relative">
      <Input data-testid={testid} type="number" min="0" step="any" value={value} onChange={onChange} className="h-8 w-full min-w-[90px] pr-6 text-right tabular" />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-emerald-700">{curSym()}</span>
    </div>
  );
}

const IconBtn = ({ icon: Icon, title, onClick, testid, disabled, primary }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled} title={title} aria-label={title}
    className={`flex h-8 w-8 items-center justify-center rounded border transition-colors active:scale-95 disabled:opacity-40 ${primary ? "border-emerald-500 bg-emerald-600 text-white hover:bg-emerald-500" : "border-slate-600 bg-slate-700 text-slate-100 hover:bg-emerald-700 hover:text-white"}`}>
    <Icon className="h-4 w-4" />
  </button>
);

export default function QuickDocDialog({ open, onOpenChange, onCreated }) {
  const [kind, setKind] = useState("earchive");
  const [customer, setCustomer] = useState(null);
  const [mName, setMName] = useState("");
  const [mScheme, setMScheme] = useState("VKN");
  const [mTaxNo, setMTaxNo] = useState("");
  const [mTaxOffice, setMTaxOffice] = useState("");
  const [mAddress, setMAddress] = useState("");
  const [note, setNote] = useState("");
  const [deductStock, setDeductStock] = useState(false);
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [colData, setColData] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [showMeta, setShowMeta] = useState(false);
  const activeIdx = useRef(0);
  const buyerRef = useRef(null);
  const custRef = useRef(null);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const [maximized, setMaximized] = useState(false);
  const [dims, setDims] = useState(null);
  const toggleMax = () => { setDims(null); setMaximized((m) => !m); };
  const startWinResize = (dir, e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX, startY = e.clientY;
    const rect = contentRef.current?.getBoundingClientRect();
    const startW = rect?.width || Math.min(1400, window.innerWidth * 0.96);
    const startH = rect?.height || window.innerHeight * 0.9;
    setMaximized(false);
    const onMove = (ev) => {
      let w = startW, h = startH;
      if (dir.includes("e")) w = startW + (ev.clientX - startX) * 2;
      if (dir.includes("w")) w = startW - (ev.clientX - startX) * 2;
      if (dir.includes("s")) h = startH + (ev.clientY - startY) * 2;
      w = Math.max(380, Math.min(window.innerWidth * 0.99, w));
      h = Math.max(320, Math.min(window.innerHeight * 0.98, h));
      setDims({ w: Math.round(w), h: Math.round(h) });
    };
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); document.body.style.userSelect = ""; };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };
  const [colW, setColW] = useState({});
  const [units, setUnits] = useState(["Adet", "Kg", "Gram", "Litre", "Koli", "Paket", "Çuval", "Teneke"]);

  useEffect(() => {
    if (!open) return;
    setApprovals([]); setApprovers([]);
    api.get("/edoc/issue-policy").then((r) => setPolicy(r.data)).catch(() => setPolicy(null));
    api.get("/edoc/columns").then((r) => setColData(r.data)).catch(() => setColData(null));
    api.get("/locations").then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setLocations(items);
      if (items.length) setLocationId((prev) => prev || items[0].id);
    }).catch(() => {});
    api.get("/settings/product-units").then((r) => { if (r.data?.units?.length) setUnits(r.data.units); }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "F12") { e.preventDefault(); setPickerQuery(""); setPickerOpen(true); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const isDespatch = kind === "despatch";
  const needApproval = !!policy && !policy.can_self_issue;
  const cat = isDespatch ? "despatch" : "invoice";
  const enabledCols = colData?.config?.[cat] || [];
  const showDesc = enabledCols.includes("description");
  const showDisc = !isDespatch && enabledCols.includes("discount");
  const customCols = enabledCols
    .filter((k) => k.startsWith("custom_"))
    .map((k) => ({ key: k, label: (colData?.custom || []).find((c) => c.key === k)?.label || k }));

  const reset = () => {
    setKind("earchive"); setCustomer(null); setMName(""); setMScheme("VKN"); setMTaxNo("");
    setMTaxOffice(""); setMAddress(""); setNote(""); setDeductStock(false); setLines([emptyLine()]);
    setApprovals([]); setApprovers([]); setShowMeta(false);
  };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const setExtra = (i, key, val) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, extras: { ...(l.extras || {}), [key]: val } } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const delLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));
  const fillLine = (i, p) => setLines((ls) => {
    const nl = ls.map((l, idx) => (idx === i ? { ...productToLine(p), extras: l.extras || {} } : l));
    if (i === nl.length - 1) nl.push(emptyLine());
    return nl;
  });

  const totals = useMemo(() => {
    let net = 0, vat = 0;
    for (const l of lines) {
      const q = Number(l.quantity) || 0, up = Number(l.unit_price) || 0, dr = Number(l.discount) || 0, vr = Number(l.vat_rate) || 0;
      const lineNet = q * up * (1 - dr / 100);
      net += lineNet; vat += lineNet * vr / 100;
    }
    return { net: Math.round(net * 100) / 100, vat: Math.round(vat * 100) / 100, grand: Math.round((net + vat) * 100) / 100 };
  }, [lines]);

  const identifier = customer ? (customer.tax_no || "") : mTaxNo;
  const today = new Date().toLocaleDateString("tr-TR");

  const exportExcel = () => {
    const head = ["SKU", "Ad", "Birim", "Miktar", `Birim Fiyat (${curSym()})`, "KDV %", "İsk %", `Tutar (${curSym()})`];
    const rows = [head];
    lines.filter((l) => l.name || l.sku).forEach((l) => {
      const q = Number(l.quantity) || 0, up = Number(l.unit_price) || 0, dr = Number(l.discount) || 0;
      rows.push([l.sku || "", l.name || "", l.unit || "", q, up, l.vat_rate, dr, Math.round(q * up * (1 - dr / 100) * 100) / 100]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "belge-kalemleri.csv"; a.click();
    toast.success("Kalemler Excel (CSV) olarak indirildi");
  };

  const focusBuyer = () => {
    buyerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    // Müşteri seçiliyse çipi vurgular; boşsa arama kutusunu açıp odaklar (imperative ref).
    setTimeout(() => custRef.current?.focusSearch(), 260);
  };

  const triggerImport = () => fileRef.current?.click();
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      let text = await f.text();
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
      const rows = text.split(/\r?\n/).filter((r) => r.trim());
      const cells = (r) => r.split(";").map((c) => c.replace(/^"|"$/g, "").replace(/""/g, '"').trim());
      const start = rows.length && /sku|mal|hizmet|ad|birim/i.test(rows[0]) ? 1 : 0;
      const num = (v) => { let s = String(v || "").trim(); if (s.includes(",")) s = s.replace(/\./g, "").replace(",", "."); return Number(s) || 0; };
      const parsed = [];
      for (let i = start; i < rows.length; i++) {
        const c = cells(rows[i]);
        if (c.length < 2) continue;
        const [sku, name, unit, qty, price, vat, disc] = c;
        if (!(name || sku)) continue;
        parsed.push({ ...emptyLine(), sku: sku || "", name: name || "", unit: unit || "Adet",
          quantity: num(qty) || 1, unit_price: num(price), vat_rate: num(vat), discount: num(disc) });
      }
      if (!parsed.length) { toast.error("İçe aktarılacak kalem bulunamadı (CSV: SKU;Ad;Birim;Miktar;Fiyat;KDV;İsk)"); return; }
      parsed.push(emptyLine());
      setLines(parsed);
      toast.success(`${parsed.length - 1} kalem içe aktarıldı`);
    } catch { toast.error("Dosya okunamadı — Excel'den 'CSV (;)' olarak kaydedin"); }
    finally { e.target.value = ""; }
  };

  const submit = async () => {
    const valid = lines.filter((l) => (l.name || "").trim() || (l.sku || "").trim());
    if (valid.length === 0) { toast.error("En az bir kalem ekleyin (ürün ara veya satır doldurun)"); return; }
    if (valid.some((l) => !(l.name || "").trim())) { toast.error("Her kalemin bir 'Ürün Adı' olmalı"); return; }
    if (kind === "efatura" && !(identifier || "").trim()) { toast.error("e-Fatura için alıcı VKN/TCKN zorunlu (e-Arşiv seçebilirsiniz)"); return; }
    if (!customer && !mName.trim() && kind !== "despatch") { toast.error("Müşteri seçin veya alıcı ünvanı yazın"); return; }
    if (needApproval && approvals.length === 0) {
      toast.error("Bu belge için yetkili onayı gerekli — 'Onay Ekle' ile onaylayın");
      setApprovalOpen(true);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        kind,
        currency: activeCurrency(),
        customer_id: customer?.id || null,
        customer_name: customer?.name || mName || null,
        customer_tax_no: customer ? (customer.tax_no || null) : (mTaxNo || null),
        customer_tax_office: customer ? (customer.tax_office || null) : (mTaxOffice || null),
        customer_address: customer ? null : (mAddress || null),
        customer_scheme: customer ? (customer.type === "corporate" ? "VKN" : "TCKN") : mScheme,
        note: note || null,
        deduct_stock: !!deductStock,
        location_id: deductStock ? (locationId || null) : undefined,
        approvals: needApproval ? approvals : undefined,
        lines: valid.map((l) => ({
          name: l.name, sku: l.sku || null, quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0, vat_rate: Number(l.vat_rate) || 0,
          unit: l.unit || "Adet", discount: Number(l.discount) || 0,
          description: (l.description || "").trim() || null,
          extras: l.extras && Object.keys(l.extras).some((k) => l.extras[k]) ? l.extras : undefined,
          product_id: l.product_id || null,
        })),
      };
      const { data } = await api.post("/e-belge/manual", payload);
      toast.success(`✓ ${data.doc_no} taslak oluşturuldu — listeden "Gönder" ile entegratöre iletin`, { duration: 5000 });
      reset();
      onOpenChange(false);
      onCreated && onCreated(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Belge oluşturulamadı"); }
    finally { setSaving(false); }
  };

  const colSpan = 5 + (showDesc ? 1 : 0) + customCols.length + (!isDespatch ? 3 + (showDisc ? 1 : 0) : 0);
  const cw = (id) => (colW[id] ? { width: colW[id] + "px" } : undefined);
  const startResize = (id, e) => {
    e.preventDefault();
    const th = e.target.closest("th");
    const startX = e.clientX, startW = th ? th.offsetWidth : 100;
    const onMove = (ev) => setColW((w) => ({ ...w, [id]: Math.max(48, startW + (ev.clientX - startX)) }));
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };
  const grip = (id) => (
    <span onMouseDown={(e) => startResize(id, e)} title="Sürükleyerek genişliği ayarla"
      className="absolute -right-px top-0 z-10 h-full w-1.5 cursor-col-resize select-none hover:bg-emerald-400/70" />
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent ref={contentRef}
          className={`flex flex-col gap-0 overflow-hidden p-0 ${dims ? "max-w-none" : (maximized ? "h-[97vh] max-h-[97vh] w-[98vw] max-w-none" : "max-h-[96vh] w-[96vw] max-w-[1400px]")}`}
          style={dims ? { width: dims.w, height: dims.h, maxWidth: "99vw", maxHeight: "98vh" } : undefined}
          data-testid="quickdoc-dialog">
          <button type="button" data-testid="quickdoc-maximize" onClick={toggleMax} title={maximized || dims ? "Küçült" : "Büyüt"}
            className="absolute right-11 top-3.5 z-20 flex h-7 w-7 items-center justify-center rounded-md text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
            {maximized || dims ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <span data-testid="quickdoc-resize-e" onMouseDown={(e) => startWinResize("e", e)} title="Sürükleyerek genişlet" className="absolute right-0 top-8 z-20 h-[calc(100%-2rem)] w-1.5 cursor-ew-resize hover:bg-emerald-400/60" />
          <span data-testid="quickdoc-resize-w" onMouseDown={(e) => startWinResize("w", e)} title="Sürükleyerek genişlet" className="absolute left-0 top-0 z-20 h-full w-1.5 cursor-ew-resize hover:bg-emerald-400/60" />
          <span data-testid="quickdoc-resize-s" onMouseDown={(e) => startWinResize("s", e)} title="Sürükleyerek uzat" className="absolute bottom-0 left-0 z-20 h-1.5 w-full cursor-ns-resize hover:bg-emerald-400/60" />
          <span data-testid="quickdoc-resize-se" onMouseDown={(e) => startWinResize("se", e)} title="Sürükleyerek boyutlandır" className="absolute bottom-0 right-0 z-30 h-3.5 w-3.5 cursor-nwse-resize bg-emerald-500/50 hover:bg-emerald-400" style={{ clipPath: "polygon(100% 0, 100% 100%, 0 100%)" }} />
          <DialogHeader className="sr-only"><DialogTitle>Belge Üret</DialogTitle></DialogHeader>

          {/* ERP üst araç çubuğu — ikon-only, kompakt */}
          <div className="flex flex-wrap items-center gap-1.5 bg-slate-800 px-3 py-2 pr-12 text-white">
            <span className="mr-1.5 flex items-center gap-1.5 font-heading text-xs font-semibold"><FilePlus2 className="h-3.5 w-3.5 text-emerald-300" /> Belge Üret</span>
            <IconBtn icon={Save} title={saving ? "Kaydediliyor…" : "Kaydet"} testid="quickdoc-tool-save" onClick={submit} disabled={saving} primary />
            <IconBtn icon={RotateCcw} title="Temizle / Yeni Belge" testid="quickdoc-tool-reset" onClick={reset} />
            <IconBtn icon={Users} title="Cari / Alıcı" testid="quickdoc-tool-cari" onClick={focusBuyer} />
            <IconBtn icon={FileSpreadsheet} title="Excel'e Aktar" testid="quickdoc-tool-excel" onClick={exportExcel} />
            <IconBtn icon={Download} title="İçe Aktar (CSV)" testid="quickdoc-tool-import" onClick={triggerImport} />
            <IconBtn icon={Search} title="Ürün Ara (F12)" testid="quickdoc-tool-search" onClick={() => { setPickerQuery(""); setPickerOpen(true); }} />
            <IconBtn icon={Settings2} title="Belge Bilgisi" testid="quickdoc-tool-settings" onClick={() => setShowMeta((v) => !v)} />
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onImportFile} data-testid="quickdoc-import-input" />
          </div>

          <div ref={scrollRef} className="relative min-h-0 flex-1 space-y-4 overflow-y-auto bg-slate-100/70 p-4">
            {/* meta şeridi */}
            {showMeta && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg bg-amber-50/70 px-3 py-2 text-xs text-slate-600 ring-1 ring-inset ring-amber-200/60" data-testid="quickdoc-meta">
                <span>📅 Belge Tarihi: <b className="text-slate-800">{today}</b></span>
                <span>💱 Para Birimi: <b className="text-emerald-700">{curSym()} {activeCurrency()}</b></span>
                <span>🧾 Senaryo: <b className="text-slate-800">{isDespatch ? "e-İrsaliye" : (kind === "efatura" ? "TEMEL FATURA" : "e-Arşiv")}</b></span>
                <span className="text-slate-400">Kolonları özelleştir → E-Belge listesindeki "Belge Kolonları"</span>
              </div>
            )}

            {/* Belge türü */}
            <div className="grid grid-cols-3 gap-2" data-testid="quickdoc-kind-group">
              {KINDS.map((o) => (
                <button key={o.k} type="button" data-testid={`quickdoc-kind-${o.k}`} onClick={() => setKind(o.k)}
                  className={`rounded-xl border p-3 text-left transition-all ${kind === o.k ? "border-emerald-600 bg-emerald-500/10 shadow-sm ring-2 ring-emerald-500/30" : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/50"}`}>
                  <p className="text-sm font-bold text-slate-800">{o.l}</p>
                  <p className="text-[11px] text-slate-400">{o.d}</p>
                </button>
              ))}
            </div>

            {/* Alıcı */}
            <div ref={buyerRef} className="rounded-xl border-l-4 border-l-emerald-500 bg-white p-3 shadow-sm ring-1 ring-slate-200">
              <Label className="text-xs font-semibold uppercase tracking-wide text-emerald-800/80">Alıcı / Müşteri (Cari)</Label>
              <CustomerSearchSelect ref={custRef} selected={customer} onSelect={setCustomer} />
              {!customer && (
                <div className="mt-2 grid gap-2 sm:grid-cols-2" data-testid="quickdoc-manual-customer">
                  <Input data-testid="quickdoc-cust-name" value={mName} onChange={(e) => setMName(e.target.value)} placeholder="Alıcı ünvanı / adı" />
                  <div className="flex gap-2">
                    <select data-testid="quickdoc-cust-scheme" value={mScheme} onChange={(e) => setMScheme(e.target.value)} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
                      <option value="VKN">VKN</option>
                      <option value="TCKN">TCKN</option>
                    </select>
                    <Input data-testid="quickdoc-cust-taxno" value={mTaxNo} onChange={(e) => setMTaxNo(e.target.value)} placeholder="Vergi / TC No" className="flex-1" />
                  </div>
                  <Input data-testid="quickdoc-cust-taxoffice" value={mTaxOffice} onChange={(e) => setMTaxOffice(e.target.value)} placeholder="Vergi Dairesi (opsiyonel)" />
                  <Input data-testid="quickdoc-cust-address" value={mAddress} onChange={(e) => setMAddress(e.target.value)} placeholder="Adres (opsiyonel)" />
                </div>
              )}
              {kind === "efatura" && !identifier.trim() && <p className="mt-1.5 text-[11px] font-medium text-amber-600">e-Fatura için VKN/TCKN gerekli — yoksa e-Arşiv seçin.</p>}
            </div>

            {/* Kalem tablosu (başlıksız, satır içi arama + F12) */}
            <div className="overflow-x-auto rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
              <table className="w-full text-sm" data-testid="quickdoc-lines">
                <colgroup>
                  <col style={cw("sku")} />
                  <col style={cw("name")} />
                  {showDesc && <col style={cw("desc")} />}
                  {customCols.map((c) => <col key={c.key} style={cw(c.key)} />)}
                  <col style={cw("unit")} />
                  <col style={cw("qty")} />
                  {!isDespatch && <>
                    <col style={cw("price")} />
                    <col style={cw("vat")} />
                    {showDisc && <col style={cw("disc")} />}
                    <col style={cw("total")} />
                  </>}
                  <col style={{ width: "42px" }} />
                </colgroup>
                <thead className="border-b border-emerald-800/15 bg-emerald-800/[0.06] text-left text-[11px] font-semibold uppercase tracking-wide text-emerald-900/70">
                  <tr>
                    <th className="relative px-2 py-2">Stok{grip("sku")}</th>
                    <th className="relative px-2 py-2">Mal / Hizmet{grip("name")}</th>
                    {showDesc && <th className="relative px-2 py-2">Açıklama{grip("desc")}</th>}
                    {customCols.map((c) => <th key={c.key} className="relative px-2 py-2">{c.label}{grip(c.key)}</th>)}
                    <th className="relative px-2 py-2">Birim{grip("unit")}</th>
                    <th className="relative px-2 py-2 text-right">Miktar{grip("qty")}</th>
                    {!isDespatch && <>
                      <th className="relative px-2 py-2 text-right">B. Fiyat {curSym()}{grip("price")}</th>
                      <th className="relative px-2 py-2 text-right">KDV{grip("vat")}</th>
                      {showDisc && <th className="relative px-2 py-2 text-right">İsk. Orn. %{grip("disc")}</th>}
                      <th className="px-2 py-2 text-right">Tutar</th>
                    </>}
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lines.map((l, i) => {
                    const q = Number(l.quantity) || 0, up = Number(l.unit_price) || 0, dr = Number(l.discount) || 0;
                    const lineNet = Math.round(q * up * (1 - dr / 100) * 100) / 100;
                    return (
                      <tr key={i} data-testid={`quickdoc-line-${i}`} className="odd:bg-white/70 even:bg-slate-50/60 transition-colors hover:bg-emerald-50/60">
                        <td className="px-2 py-1"><Input data-testid={`quickdoc-line-sku-${i}`} value={l.sku} onFocus={() => (activeIdx.current = i)} onChange={(e) => setLine(i, { sku: e.target.value })} className="h-8 w-full min-w-[70px] font-mono text-xs" placeholder="SKU" /></td>
                        <td className="px-2 py-1">
                          <NameAutocomplete testid={`quickdoc-line-name-${i}`} value={l.name}
                            containerRef={scrollRef}
                            onFocus={() => (activeIdx.current = i)}
                            onChange={(v) => setLine(i, { name: v })}
                            onPick={(p) => fillLine(i, p)}
                            onBinoculars={() => { activeIdx.current = i; setPickerQuery(l.name || ""); setPickerOpen(true); }} />
                        </td>
                        {showDesc && <td className="px-2 py-1"><Input data-testid={`quickdoc-line-desc-${i}`} value={l.description || ""} onChange={(e) => setLine(i, { description: e.target.value })} className="h-8 w-full min-w-[100px]" placeholder="Açıklama" /></td>}
                        {customCols.map((c) => (
                          <td key={c.key} className="px-2 py-1"><Input data-testid={`quickdoc-line-${c.key}-${i}`} value={(l.extras || {})[c.key] || ""} onChange={(e) => setExtra(i, c.key, e.target.value)} className="h-8 w-full min-w-[90px]" placeholder={c.label} /></td>
                        ))}
                        <td className="px-2 py-1">
                          <select data-testid={`quickdoc-line-unit-${i}`} value={l.unit || "Adet"} onChange={(e) => setLine(i, { unit: e.target.value })} className="h-8 w-full min-w-[72px] rounded-md border border-input bg-white px-1 text-sm">
                            {(l.unit && !units.includes(l.unit) ? [l.unit, ...units] : units).map((u) => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1"><Input data-testid={`quickdoc-line-qty-${i}`} type="number" min="0" step="any" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="h-8 w-full min-w-[64px] text-right" /></td>
                        {!isDespatch && <>
                          <td className="px-2 py-1"><MoneyInput testid={`quickdoc-line-price-${i}`} value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} /></td>
                          <td className="px-2 py-1">
                            <select data-testid={`quickdoc-line-vat-${i}`} value={l.vat_rate} onChange={(e) => setLine(i, { vat_rate: Number(e.target.value) })} className="h-8 w-full min-w-[56px] rounded-md border border-input bg-white px-1 text-right text-sm">
                              {VATS.map((v) => <option key={v} value={v}>%{v}</option>)}
                            </select>
                          </td>
                          {showDisc && <td className="px-2 py-1"><Input data-testid={`quickdoc-line-disc-${i}`} type="number" min="0" max="100" step="any" value={l.discount} onChange={(e) => setLine(i, { discount: e.target.value })} className="h-8 w-full min-w-[56px] text-right" /></td>}
                          <td className="px-2 py-1 text-right font-semibold tabular text-slate-800">{fmtTL(lineNet)}</td>
                        </>}
                        <td className="px-2 py-1 text-right">
                          <button type="button" data-testid={`quickdoc-line-del-${i}`} onClick={() => delLine(i)} className="rounded-sm p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={colSpan} className="px-2 py-1.5">
                      <button type="button" data-testid="quickdoc-add-line" onClick={addLine} className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-emerald-300 bg-emerald-50/50 px-3 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100/70">
                        <Plus className="h-3.5 w-3.5" /> Satır <span className="hidden text-emerald-500/70 sm:inline">· F12 ile ürün ara</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Not + stok + toplam */}
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2 rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
                <Input data-testid="quickdoc-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (opsiyonel)" />
                {!isDespatch && (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-slate-600" data-testid="quickdoc-deduct-stock-label">
                      <input type="checkbox" data-testid="quickdoc-deduct-stock" checked={deductStock} onChange={(e) => setDeductStock(e.target.checked)} className="h-4 w-4" />
                      Kalemleri stoktan da düş (ürün eşleşenler)
                    </label>
                    {deductStock && locations.length > 0 && (
                      <select data-testid="quickdoc-deduct-location" value={locationId} onChange={(e) => setLocationId(e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                        {locations.map((L) => <option key={L.id} value={L.id}>{L.name}</option>)}
                      </select>
                    )}
                  </div>
                )}
              </div>
              {!isDespatch && (
                <div className="rounded-xl border border-emerald-800 bg-emerald-950 p-4 text-sm text-white shadow-md" data-testid="quickdoc-totals">
                  <div className="flex justify-between py-0.5"><span className="text-emerald-200/80">Ara Toplam (KDV hariç)</span><span className="tabular font-semibold">{fmtTL(totals.net)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-emerald-200/80">Toplam KDV</span><span className="tabular font-semibold">{fmtTL(totals.vat)}</span></div>
                  <div className="mt-1.5 flex items-center justify-between border-t border-emerald-800 pt-2"><span className="font-bold">Genel Toplam</span><span data-testid="quickdoc-grand-total" className="tabular text-xl font-extrabold text-emerald-300">{fmtTL(totals.grand)}</span></div>
                </div>
              )}
            </div>

            {needApproval && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="quickdoc-approval-panel">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-800"><ShieldCheck className="h-4 w-4" /> Yetkili Onayı Gerekli</p>
                    <p className="mt-0.5 text-[11px] text-amber-700/80">
                      {policy?.peer_pool_total > 0
                        ? `En az ${policy.peer_need} adet 5★ yetkili onayı ya da bir Yönetici (PIN/şifre/kart).`
                        : "Bir Yönetici onayı gerekli (PIN/şifre/kart)."}
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" data-testid="quickdoc-add-approval" onClick={() => setApprovalOpen(true)} className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100">
                    <ShieldCheck className="mr-1 h-4 w-4" /> Onay Ekle
                  </Button>
                </div>
                {approvers.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="quickdoc-approvers">
                    {approvers.map((a, i) => (
                      <span key={i} data-testid={`quickdoc-approver-${i}`} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                        <CheckCircle2 className="h-3 w-3" /> {a.name} · {a.role_label}
                        <button type="button" data-testid={`quickdoc-approver-del-${i}`} onClick={() => { setApprovals((ls) => ls.filter((_, idx) => idx !== i)); setApprovers((ls) => ls.filter((_, idx) => idx !== i)); }} className="ml-0.5 rounded-full p-0.5 hover:bg-red-50 hover:text-red-500"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Vazgeç</Button>
              <Button type="button" data-testid="quickdoc-submit" onClick={submit} disabled={saving} className="bg-emerald-800 text-white hover:bg-emerald-700">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FilePlus2 className="mr-2 h-4 w-4" />} Belge Üret
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ProductPickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} initialQuery={pickerQuery}
        onPick={(p) => fillLine(Math.min(activeIdx.current, lines.length - 1), p)} />

      <ApprovalDialog open={approvalOpen} onClose={() => setApprovalOpen(false)}
        permission="edoc.issue" title="Belge Kesme Onayı"
        description="Bu belgeyi kesmek için yetkili (5★ yetkili çoğunluğu veya Yönetici) onayı gerekiyor — PIN / şifre / kart."
        onApproved={(cred, approver) => { setApprovals((a) => [...a, cred]); setApprovers((a) => [...a, approver]); }} />
    </>
  );
}
